import { DndContext, DragOverlay, useDndContext } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { requireService, wrap } from '@repo/di-ez';
import { ArrowDropDown, EyeLight, MoveCustom, ViewHideLight } from '@repo/go-icons';
import type { GridApi, GridColDef, GridColumnGroup, GridColumnNode, GridColumnVisibilityModel } from '@repo/go-table';
import { Button, Collapse, Drawer, Scrollbar, Switch } from '@repo/go-ui';
import { clsx } from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Dispatch, MutableRefObject, ReactElement, ReactNode, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';

import { DisplaySettingItem } from 'src/shared/components/display-settings-item';
import { hasValue } from 'src/shared/utils/common';

import type { DisplayManager } from './display-manager';
import { MIN_VISIBLE_COLUMNS } from './display-manager';
import styles from './table-settings.module.scss';

type Props = {
  displayManager: DisplayManager;
  columns: GridColDef[];
  groups: GridColumnGroup[];
  tableApi: MutableRefObject<GridApi>;
};

type TRenderGroup = {
  children: Record<string, GridColDef | TRenderGroup>;
  group: GridColumnGroup;
};

const isTRenderGroup = (item: TRenderGroup | GridColDef | GridColumnNode): item is TRenderGroup => {
  return 'children' in item;
};

const isGridGroup = (item: GridColDef | GridColumnNode): item is GridColumnGroup => {
  return 'children' in item;
};

const HIDDEN_IN_SETTINGS_SIDEBAR_COLUMNS: string[] = [];

const CustomDragOverlay = wrap<{
  columnsMapByGroupId: Record<
    string,
    GridColumnGroup & {
      parentGroup?: GridColumnGroup;
    }
  >;
  columnsMap: Record<string, GridColDef>;
  renderColumnItem: (column: GridColDef, isOverlay: boolean) => ReactElement;
  displayManager: DisplayManager;
}>(function CustomDragOverlay({ columnsMapByGroupId, columnsMap, renderColumnItem, displayManager }) {
  const { active } = useDndContext();

  const activeElement = (() => {
    if (!active) {
      return null;
    }

    const asGroup = columnsMapByGroupId[active.id.toString()];

    if (asGroup) {
      return asGroup;
    }

    return columnsMap[active.id.toString()] ?? null;
  })();

  const renderedActiveElement = (() => {
    if (!activeElement) {
      return null;
    }

    if ('groupId' in activeElement) {
      return (
        <RenderStructuredGroupItem
          overlay
          item={activeElement}
          columnsMapByGroupId={columnsMapByGroupId}
          displayManager={displayManager}
        />
      );
    }

    return renderColumnItem(activeElement, true);
  })();

  return <DragOverlay>{renderedActiveElement}</DragOverlay>;
});

const RenderStructuredGroupItem = wrap<
  | {
      overlay: true;
      item: GridColumnGroup & {
        parentGroup?: GridColumnGroup;
      };
      activeGroups?: Record<string, boolean>;
      setActiveGroups?: Dispatch<SetStateAction<Record<string, boolean>>>;
      renderColumnItem?: (column: GridColDef) => ReactElement;
      columnsMapByGroupId: Record<string, GridColumnGroup & { parentGroup?: GridColumnGroup }>;
      apiRef?: MutableRefObject<GridApi>;
      handleColumnVisibilityChange?: (newModel: Record<string, boolean>) => void;
      displayManager: DisplayManager;
    }
  | {
      overlay: false;
      item: TRenderGroup;
      activeGroups: Record<string, boolean>;
      setActiveGroups: Dispatch<SetStateAction<Record<string, boolean>>>;
      renderColumnItem: (column: GridColDef) => ReactElement;
      columnsMapByGroupId: Record<string, GridColumnGroup & { parentGroup?: GridColumnGroup }>;
      apiRef: MutableRefObject<GridApi>;
      handleColumnVisibilityChange: (newModel: Record<string, boolean>) => void;
      displayManager: DisplayManager;
    }
>(function RenderStructuredGroupItem({
  overlay,
  item,
  activeGroups,
  setActiveGroups,
  renderColumnItem,
  columnsMapByGroupId,
  apiRef,
  handleColumnVisibilityChange,
  displayManager,
}) {
  const timerRef = useRef<NodeJS.Timeout>();
  const key = overlay ? item.groupId : isTRenderGroup(item) ? item.group.groupId : v4();

  const { attributes, listeners, transform, transition, isDragging, active, setNodeRef } = useSortable({ id: key });

  const isActive = overlay ? false : !!activeGroups?.[item.group.groupId];
  const isTemporaryHidden = (() => {
    if (!active) {
      return false;
    }

    if (overlay || isDragging) {
      return true;
    }

    const activeGroup = (() => {
      const asGroup = columnsMapByGroupId[active.id.toString()];

      if (asGroup) {
        return asGroup;
      }

      // Если не нашлась группа, значит перетаскивается колонка, поэтому для неё вручнгую находим родительскую группу
      const groupPath = apiRef.current.getColumnGroupPath(active.id.toString());

      if (!groupPath.length) {
        return null;
      }

      return { parentGroup: columnsMapByGroupId[groupPath[groupPath.length - 1]] };
    })();
    const itemGroup = columnsMapByGroupId[item.group.groupId];

    return (activeGroup?.parentGroup ?? null) === (itemGroup?.parentGroup ?? null);
  })();

  const [isVisible, setIsVisible] = useState(isActive && !isTemporaryHidden);

  const style = {
    transform: transform ? `translate(0, ${transform.y}px) scaleX(1) scaleY(1)` : undefined,
    transition,
  };

  const children = useMemo(() => Object.values(item.children), [item.children]);
  const groupChildren = (() => {
    const rawChildren = overlay ? columnsMapByGroupId?.[item.groupId].children : item.group.children;

    const processNestedElements = (acc: string[], elements: GridColumnNode[]) => {
      elements.forEach((element) => {
        const isGroup = isGridGroup(element);

        if (isGroup) {
          return processNestedElements(acc, element.children);
        }

        acc.push((element as GridColDef).field);
      });

      return acc;
    };

    return processNestedElements([], rawChildren);
  })();

  const isSwitchActive = groupChildren.some((child) => displayManager.settings.columnsVisibility?.[child] !== false);

  const handleSwitchChange = useCallback(
    (value: boolean) => {
      const newModel = groupChildren.reduce(
        (acc, child) => {
          acc[child] = value;
          return acc;
        },
        {} as Record<string, boolean>,
      );

      handleColumnVisibilityChange?.(newModel);
    },
    [groupChildren, handleColumnVisibilityChange],
  );

  useEffect(() => {
    if (isActive && !isTemporaryHidden) {
      setIsVisible(isActive);
    } else {
      timerRef.current = setTimeout(() => {
        setIsVisible(false);
      }, 400);

      return () => {
        clearTimeout(timerRef.current);
      };
    }
  }, [isActive, isTemporaryHidden]);

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      style={{
        ...style,
        backgroundColor: 'var(--go-drawer-bg)',
        zIndex: isDragging ? 10 : 1,
        position: 'relative',
        visibility: isDragging ? 'hidden' : 'visible',
      }}
    >
      <Collapse.Panel
        key={overlay ? item.groupId : item.group.groupId}
        panelKey={overlay ? item.groupId : item.group.groupId}
        header={
          <div className={styles.groupHeader}>
            <div className={styles.groupHeaderActions}>
              <Button
                variant="flat"
                {...listeners}
                className={clsx(styles.draggingButton, overlay && styles.draggingButton__active)}
              >
                <MoveCustom style={{ color: 'var(--go-text)' }} />
              </Button>
              <Switch
                leftNode={<ViewHideLight />}
                rightNode={<EyeLight />}
                checked={isSwitchActive}
                size="m"
                onChange={(value) => handleSwitchChange(value)}
              />
            </div>
            <div
              className={styles.groupHeaderContent}
              onClick={() =>
                overlay
                  ? undefined
                  : setActiveGroups?.((prev) => {
                      return { ...prev, [item.group.groupId]: !prev[item.group.groupId] };
                    })
              }
            >
              <p className={styles.groupHeaderTitle}>{overlay ? item.headerName : item.group.headerName}</p>
              <ArrowDropDown
                style={{
                  transform: isActive ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease-in-out',
                }}
              />
            </div>
          </div>
        }
        isActive={isActive && !isTemporaryHidden}
      >
        {!overlay && isVisible && (
          <SortableContext
            strategy={verticalListSortingStrategy}
            items={children.map((child) => {
              if (isTRenderGroup(child)) {
                return child.group.groupId;
              }

              return child.field;
            })}
          >
            <RenderStructuredItems
              items={children}
              displayManager={displayManager}
              renderColumnItem={renderColumnItem}
              activeGroups={activeGroups}
              setActiveGroups={setActiveGroups}
              columnsMapByGroupId={columnsMapByGroupId}
              apiRef={apiRef}
              handleColumnVisibilityChange={handleColumnVisibilityChange}
            />
          </SortableContext>
        )}
      </Collapse.Panel>
    </div>
  );
});

const RenderStructuredItems = wrap<{
  items: (TRenderGroup | GridColDef)[];
  renderColumnItem: (column: GridColDef) => ReactElement;
  activeGroups: Record<string, boolean>;
  setActiveGroups: Dispatch<SetStateAction<Record<string, boolean>>>;
  columnsMapByGroupId: Record<string, GridColumnGroup & { parentGroup?: GridColumnGroup }>;
  apiRef: MutableRefObject<GridApi>;
  handleColumnVisibilityChange: (newModel: Record<string, boolean>) => void;
  displayManager: DisplayManager;
}>(function RenderStructuredItems({
  items,
  renderColumnItem,
  activeGroups,
  setActiveGroups,
  columnsMapByGroupId,
  apiRef,
  handleColumnVisibilityChange,
  displayManager,
}) {
  return (
    <SortableContext
      strategy={verticalListSortingStrategy}
      items={items.map((item) => {
        if (isTRenderGroup(item)) {
          return item.group.groupId;
        }

        return item.field;
      })}
    >
      {items.map((item) => {
        const key = isTRenderGroup(item) ? item.group.groupId : item.field;

        const isGroup = isTRenderGroup(item);

        if (!isGroup) {
          return renderColumnItem(item);
        }

        return (
          <RenderStructuredGroupItem
            key={key}
            overlay={false}
            item={item}
            activeGroups={activeGroups}
            setActiveGroups={setActiveGroups}
            renderColumnItem={renderColumnItem}
            columnsMapByGroupId={columnsMapByGroupId}
            apiRef={apiRef}
            handleColumnVisibilityChange={handleColumnVisibilityChange}
            displayManager={displayManager}
          />
        );
      })}
    </SortableContext>
  );
});

export const WellsTableSettings = wrap<Props>(function WellsTableSettings({
  displayManager,
  columns,
  groups,
  tableApi,
}) {
  const { t } = useTranslation();
  const wellsPageStore = requireService('wellsPageStore');
  const [activeGroups, setActiveGroups] = useState<Record<string, boolean>>({});
  const columnsMap = useMemo(() => {
    return columns.reduce(
      (acc, col) => {
        acc[col.field] = col;
        return acc;
      },
      {} as Record<string, GridColDef>,
    );
  }, [columns]);

  const columnsOrderModel = displayManager.settings.columnsOrderModel;

  const sortByOrderModel = useCallback(
    (columns: string[]) => {
      if (!columnsOrderModel) {
        return columns;
      }

      const copiedColumns = [...columns];

      return copiedColumns.sort((a, b) => {
        const c1index = columnsOrderModel.indexOf(a);
        const c2index = columnsOrderModel.indexOf(b);

        if (c1index < 0 && c2index < 0) {
          return 0;
        }

        if (c1index < 0) {
          return -1;
        }

        if (c2index < 0) {
          return 1;
        }

        return c1index - c2index;
      });
    },
    [columnsOrderModel],
  );

  const { leftPinned, free, rightPinned } = useMemo(() => {
    const { left, right } = displayManager.settings.pinnedColumns ?? {};

    const { leftPinned, free, rightPinned } = columns.reduce(
      (
        acc: {
          leftPinned: GridColDef[];
          free: GridColDef[];
          rightPinned: GridColDef[];
        },
        col,
      ) => {
        if (left?.includes(col.field)) {
          acc.leftPinned.push(col);
          return acc;
        }

        if (right?.includes(col.field)) {
          acc.rightPinned.push(col);
          return acc;
        }

        acc.free.push(col);

        return acc;
      },
      {
        leftPinned: [],
        free: [],
        rightPinned: [],
      },
    );

    if (columnsOrderModel?.length) {
      free.sort((a, b) => {
        const c1index = columnsOrderModel.indexOf(a.field);
        const c2index = columnsOrderModel.indexOf(b.field);

        if (c1index < 0 && c2index < 0) {
          return 0;
        }

        if (c1index < 0) {
          return -1;
        }

        if (c2index < 0) {
          return 1;
        }

        return c1index - c2index;
      });
    }

    return { leftPinned, free, rightPinned, orderedColumns: [...leftPinned, ...free, ...rightPinned] };
  }, [columns, columnsOrderModel, displayManager.settings.pinnedColumns]);

  const columnsMapByGroupId = useMemo(
    () =>
      groups.reduce(
        (acc, currentGroup) => {
          const processChildren = (group: GridColumnGroup & { parentGroup?: GridColumnGroup }) => {
            group.children = group.children.map((child) => {
              if ('groupId' in child) {
                return processChildren({
                  ...child,
                  parentGroup: group,
                  children: child.children.filter((child) => {
                    if ('field' in child) {
                      return columnsMap[child.field] !== undefined;
                    }

                    return true;
                  }),
                });
              } else {
                return child;
              }
            });

            acc[group.groupId] = group;
            return group;
          };

          processChildren(currentGroup);

          return acc;
        },
        {} as Record<string, GridColumnGroup & { parentGroup?: GridColumnGroup }>,
      ),
    [columnsMap, groups],
  );

  const handleColumnVisibilityChange = useCallback(
    (newModel: Record<string, boolean>) => {
      const { settings } = displayManager;

      const newVisibilityModel: GridColumnVisibilityModel = {
        ...settings.columnsVisibility,
        ...newModel,
      };

      displayManager.onColumnVisibilityChange(newVisibilityModel);
    },
    [displayManager],
  );

  const handleUnpinClick = useCallback(
    (field: string) => {
      tableApi.current.unpinColumn(field);
    },
    [tableApi],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { settings } = displayManager;

      const savedColumnsOrderModel = settings?.columnsOrderModel ?? [];
      const columnsOrderModel = [...columns.map((c) => c.field)].sort((a, b) => {
        const c1index = savedColumnsOrderModel.indexOf(a);
        const c2index = savedColumnsOrderModel.indexOf(b);

        if (c1index < 0 && c2index < 0) {
          return 0;
        }

        if (c1index < 0) {
          return -1;
        }

        if (c2index < 0) {
          return 1;
        }

        return c1index - c2index;
      });

      const activeElement = event.active.id;
      const overElement = event.over?.id;

      if (!hasValue(overElement) || !hasValue(activeElement) || activeElement === overElement) {
        return;
      }

      const activeElementAsGroup = columnsMapByGroupId[activeElement.toString()];
      const overElementAsGroup = columnsMapByGroupId[overElement.toString()];

      if (!activeElementAsGroup && !overElementAsGroup) {
        const newIndex = columnsOrderModel.findIndex((item) => item === overElement);
        const oldIndex = columnsOrderModel.findIndex((item) => item === activeElement);

        const reorderedColumns = arrayMove(columnsOrderModel, oldIndex, newIndex);

        displayManager.onColumnsOrderModelChange(reorderedColumns);
        return;
      }

      const activeElements = (() => {
        if (!activeElementAsGroup) {
          return [activeElement.toString()];
        }

        const processNestedElements = (acc: string[], elements: GridColumnNode[]) => {
          elements.forEach((element) => {
            const isGroup = isGridGroup(element);

            if (isGroup) {
              return processNestedElements(acc, element.children);
            }

            acc.push((element as GridColDef).field);
          });

          return acc;
        };

        const result = processNestedElements([], activeElementAsGroup.children);

        return sortByOrderModel(result);
      })();

      const targetIndex = (() => {
        if (!overElementAsGroup) {
          return columnsOrderModel.findIndex((item) => item === overElement);
        }

        const processNestedElements = (acc: string[], elements: GridColumnNode[]) => {
          elements.forEach((element) => {
            const isGroup = isGridGroup(element);

            if (isGroup) {
              return processNestedElements(acc, element.children);
            }

            acc.push((element as GridColDef).field);
          });

          return acc;
        };

        const overElements = sortByOrderModel(processNestedElements([], overElementAsGroup.children));

        const isToTopOrBottom = (() => {
          const firstActiveElementIndex = columnsOrderModel.indexOf(activeElements[0]);
          const firstOverElementIndex = columnsOrderModel.indexOf(overElements[0]);

          return firstActiveElementIndex > firstOverElementIndex ? 'top' : 'bottom';
        })();

        const index = isToTopOrBottom === 'top' ? 0 : overElements.length - 1;

        const result = overElements[index];

        if (!result || !result.length) {
          return null;
        }

        const overElementIndex = columnsOrderModel.indexOf(result);

        return isToTopOrBottom === 'top' ? overElementIndex : overElementIndex + 1;
      })();

      if (!hasValue(targetIndex)) {
        console.error('target index not found');
        return;
      }

      const copiedColumnsOrderModel: (string | null)[] = [...columnsOrderModel];

      copiedColumnsOrderModel.splice(
        columnsOrderModel.indexOf(activeElements[0]),
        activeElements.length,
        ...new Array(activeElements.length).fill(null),
      );

      copiedColumnsOrderModel.splice(targetIndex, 0, ...activeElements);

      const resultColumnsOrderModel = copiedColumnsOrderModel.filter((item) => hasValue(item));

      displayManager.onColumnsOrderModelChange(resultColumnsOrderModel);
    },
    [columns, columnsMapByGroupId, displayManager, sortByOrderModel],
  );

  const checkIsColumnVisibilityDisabled = useCallback(
    (columnField: string): boolean => {
      const { settings } = displayManager;

      if (!settings.columnsOrderModel) return false;

      const visibleColumns = settings.columnsVisibility;

      if (!visibleColumns) return false;

      const isColumnVisible = visibleColumns[columnField] !== false;
      let visibleColumnsCount = 0;

      settings.columnsOrderModel.forEach((item) => {
        if (visibleColumns[item] !== false) {
          visibleColumnsCount += 1;
        }
      });

      const columnDef = columns.find((column) => column.field === columnField);

      if (!columnDef) return true;

      if (columnDef.hideable === false) {
        return true;
      } else {
        if (!isColumnVisible) {
          return false;
        }

        return visibleColumnsCount <= MIN_VISIBLE_COLUMNS;
      }
    },
    [columns, displayManager],
  );

  const renderColumnItem = useCallback(
    (column: GridColDef, isOverlay?: boolean) => {
      if (HIDDEN_IN_SETTINGS_SIDEBAR_COLUMNS.includes(column.field)) {
        return <></>;
      }

      const fieldName = column.headerName;

      const title = fieldName;
      const isPinned = displayManager.displaySettingPinnedColumns.includes(column.field);

      return (
        <DisplaySettingItem
          key={column.field}
          overlay={isOverlay}
          field={column.field}
          title={title}
          style={{ backgroundColor: 'var(--go-drawer-bg)' }}
          unpinColumn={handleUnpinClick}
          isVisibilitySwitcherDisabled={checkIsColumnVisibilityDisabled(column.field)}
          isVisible={
            displayManager.settings.columnsVisibility?.[column.field] === undefined
              ? true
              : displayManager.settings.columnsVisibility[column.field]
          }
          isDragDisabled={isPinned}
          isPinned={isPinned}
          onColumnVisibilityChange={(column, value) => handleColumnVisibilityChange({ [column]: value })}
        />
      );
    },
    [
      checkIsColumnVisibilityDisabled,
      displayManager.displaySettingPinnedColumns,
      displayManager.settings.columnsVisibility,
      handleColumnVisibilityChange,
      handleUnpinClick,
    ],
  );

  const renderItems = useCallback(
    (renderedColumns: GridColDef[]): ReactNode => {
      const groupsMap: Record<string, TRenderGroup | GridColDef> = {};

      renderedColumns.forEach((column) => {
        if (HIDDEN_IN_SETTINGS_SIDEBAR_COLUMNS.includes(column.field)) {
          return;
        }

        const groupPath = tableApi.current.getColumnGroupPath(column.field);

        if (!groupPath.length) {
          groupsMap[column.field] = column;
        } else {
          groupPath.reduce((acc: Record<string, GridColDef | TRenderGroup>, group, index) => {
            const groupObj = columnsMapByGroupId[group];

            if (!acc[group]) {
              acc[group] = { children: {}, group: groupObj ?? null };
            }

            const _group = acc[group] as TRenderGroup;

            if (index === groupPath.length - 1) {
              _group.children[column.field] = column;

              return acc;
            }

            return _group.children;
          }, groupsMap);
        }
      });

      const structuredItems = Object.values(groupsMap);

      return (
        <RenderStructuredItems
          items={structuredItems}
          activeGroups={activeGroups}
          columnsMapByGroupId={columnsMapByGroupId}
          apiRef={tableApi}
          handleColumnVisibilityChange={handleColumnVisibilityChange}
          setActiveGroups={setActiveGroups}
          renderColumnItem={renderColumnItem}
          displayManager={displayManager}
        />
      );
    },
    [activeGroups, columnsMapByGroupId, displayManager, handleColumnVisibilityChange, renderColumnItem, tableApi],
  );

  return (
    <Drawer
      open={wellsPageStore.isTableSettingsOpen}
      onClose={() => wellsPageStore.setIsTableSettingsOpen(false)}
      style={{ width: 400 }}
      title={t('common:displaySettings')}
    >
      <Scrollbar>
        <div className={styles.columnsWrapper}>
          {renderItems(leftPinned)}
          <DndContext modifiers={[restrictToVerticalAxis, restrictToParentElement]} onDragEnd={handleDragEnd}>
            <div>{renderItems(free)}</div>
            <CustomDragOverlay
              columnsMapByGroupId={columnsMapByGroupId}
              columnsMap={columnsMap}
              renderColumnItem={renderColumnItem}
              displayManager={displayManager}
            />
          </DndContext>
          {renderItems(rightPinned)}
        </div>
      </Scrollbar>
    </Drawer>
  );
});
